'use client'

import * as React from 'react'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuthContext } from '@/hooks/use-auth-context'
import {
  changePassword,
  confirmEmailChange,
  fetchProfile,
  type IdentitySummary,
  type ProfilePatch,
  requestEmailChange,
  setPassword,
  unlinkGoogle,
  updateProfile,
  uploadAvatar,
} from '@/lib/self-auth/account-actions'

const MIN_PASSWORD_LENGTH = 8
/** Mirrors the backend's LOGIN_CODE_RESEND_COOLDOWN. */
const EMAIL_CODE_RESEND_SECONDS = 60
const EMAIL_CODE_LENGTH = 6
/** Shape check only; the backend is the authority on deliverability. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Small brand mark for the Google link/unlink controls. */
function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="size-4">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"
      />
    </svg>
  )
}

interface AccountSettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Self-auth account settings modal: edit name + avatar, change the sign-in
 * email, link/unlink Google, or set/change the password. Opened from the
 * nav-user dropdown. All calls go to `/api/auth/*` with a Bearer token, except
 * linking Google which is a full-page redirect through the BFF
 * `/api/session/oauth/google/link-begin`.
 *
 * Setting or changing the password revokes every session, so on success the
 * user is signed out and sent to /sign-in; changing the email ends the same
 * way, for the same reason. `fetchProfile` is the single source of profile
 * truth here (name/avatar/email + has_password + identities); the name fields
 * seed from the real first/last name (null → empty is correct), and the
 * always-present email anchors the Change control so the dialog is never blank.
 */
export function AccountSettingsDialog({
  open,
  onOpenChange,
}: AccountSettingsDialogProps) {
  const t = useTranslations('AccountSettings')
  const { user, getToken, signOut, refreshIdentity } = useAuthContext()

  const [email, setEmail] = React.useState('')
  const [firstName, setFirstName] = React.useState('')
  const [lastName, setLastName] = React.useState('')
  const [avatarUrl, setAvatarUrl] = React.useState<string | null>(null)
  const [pendingAvatarKey, setPendingAvatarKey] = React.useState<string | null>(
    null,
  )
  const [hasPassword, setHasPassword] = React.useState<boolean | null>(null)
  const [googleIdentity, setGoogleIdentity] =
    React.useState<IdentitySummary | null>(null)
  const [profileLoading, setProfileLoading] = React.useState(false)
  const [savingProfile, setSavingProfile] = React.useState(false)
  const [uploadingAvatar, setUploadingAvatar] = React.useState(false)
  const [unlinking, setUnlinking] = React.useState(false)

  const [currentPassword, setCurrentPassword] = React.useState('')
  const [newPassword, setNewPassword] = React.useState('')
  const [confirmPassword, setConfirmPassword] = React.useState('')
  const [changingPassword, setChangingPassword] = React.useState(false)

  // Email change: 'idle' shows the address, 'enter' collects the new one,
  // 'code' redeems what was mailed to it.
  const [emailStep, setEmailStep] = React.useState<'idle' | 'enter' | 'code'>(
    'idle',
  )
  const [newEmail, setNewEmail] = React.useState('')
  const [emailCode, setEmailCode] = React.useState('')
  const [emailResendIn, setEmailResendIn] = React.useState(0)
  const [requestingEmail, setRequestingEmail] = React.useState(false)
  const [confirmingEmail, setConfirmingEmail] = React.useState(false)

  // Full-page link entry point, seeded on the client so we can carry the
  // current path back after the OAuth round-trip re-opens this dialog.
  const [linkGoogleHref, setLinkGoogleHref] = React.useState(
    '/api/session/oauth/google/link-begin',
  )

  const fileInputRef = React.useRef<HTMLInputElement>(null)
  // Blob: URL of the local avatar preview, held so we can revoke the previous
  // one on replace / save / unmount instead of leaking it.
  const avatarObjectUrlRef = React.useRef<string | null>(null)

  const revokeAvatarPreview = React.useCallback(() => {
    if (avatarObjectUrlRef.current) {
      URL.revokeObjectURL(avatarObjectUrlRef.current)
      avatarObjectUrlRef.current = null
    }
  }, [])

  const loadProfile = React.useCallback(async () => {
    const token = await getToken()
    if (!token) {
      setProfileLoading(false)
      return
    }
    const res = await fetchProfile(token)
    if (res.ok) {
      setEmail(res.profile.email)
      setFirstName(res.profile.first_name ?? '')
      setLastName(res.profile.last_name ?? '')
      revokeAvatarPreview()
      setAvatarUrl(res.profile.avatar_url)
      setPendingAvatarKey(null)
      setHasPassword(res.profile.has_password)
      setGoogleIdentity(
        res.profile.identities.find(i => i.provider === 'google') ?? null,
      )
    } else {
      toast.error(t('loadError'))
    }
    setProfileLoading(false)
  }, [getToken, revokeAvatarPreview, t])

  // Seed profile from /me each time the dialog opens.
  React.useEffect(() => {
    if (!open) return
    setProfileLoading(true)
    void loadProfile()
  }, [open, loadProfile])

  // Refresh the link entry point with the current path so the OAuth flow lands
  // back here (?account=1&linked=google) after completing.
  React.useEffect(() => {
    if (!open) return
    const returnPath = window.location.pathname + window.location.search
    setLinkGoogleHref(
      `/api/session/oauth/google/link-begin?redirect_url=${encodeURIComponent(returnPath)}`,
    )
  }, [open])

  // Clear password fields and any half-finished email change when the dialog
  // closes. The code row stays live server-side for its 10 minutes, so
  // reopening and requesting again just resumes within the cooldown.
  React.useEffect(() => {
    if (!open) {
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setEmailStep('idle')
      setNewEmail('')
      setEmailCode('')
      setEmailResendIn(0)
    }
  }, [open])

  // Resend cooldown ticker (code step only).
  React.useEffect(() => {
    if (emailResendIn <= 0) return
    const timer = setInterval(() => {
      setEmailResendIn(value => (value <= 1 ? 0 : value - 1))
    }, 1000)
    return () => clearInterval(timer)
  }, [emailResendIn])

  // Release any outstanding avatar preview blob on unmount.
  React.useEffect(() => revokeAvatarPreview, [revokeAvatarPreview])

  const initial = (firstName || email || user?.email || 'U')
    .charAt(0)
    .toUpperCase()

  const handleAvatarSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    event.target.value = '' // allow re-selecting the same file
    if (!file) return
    const token = await getToken()
    if (!token) {
      toast.error(t('sessionExpired'))
      return
    }
    setUploadingAvatar(true)
    const res = await uploadAvatar(token, file)
    setUploadingAvatar(false)
    if (!res.ok) {
      toast.error(
        res.code === 'unsupported_media_type'
          ? t('avatarTypeError')
          : t('avatarUploadError'),
      )
      return
    }
    setPendingAvatarKey(res.objectKey)
    revokeAvatarPreview() // drop the previous preview blob before minting a new one
    const previewUrl = URL.createObjectURL(file) // local preview until saved
    avatarObjectUrlRef.current = previewUrl
    setAvatarUrl(previewUrl)
    toast.success(t('avatarReady'))
  }

  const handleSaveProfile = async () => {
    const token = await getToken()
    if (!token) {
      toast.error(t('sessionExpired'))
      return
    }
    const patch: ProfilePatch = {
      first_name: firstName.trim() || null,
      last_name: lastName.trim() || null,
    }
    if (pendingAvatarKey) patch.avatar_object_key = pendingAvatarKey
    setSavingProfile(true)
    const res = await updateProfile(token, patch)
    setSavingProfile(false)
    if (!res.ok) {
      toast.error(t('saveError'))
      return
    }
    revokeAvatarPreview() // server URL now authoritative; drop the local preview blob
    setAvatarUrl(res.profile.avatar_url)
    setPendingAvatarKey(null)
    await refreshIdentity()
    toast.success(t('saved'))
  }

  const handleUnlinkGoogle = async () => {
    const token = await getToken()
    if (!token) {
      toast.error(t('sessionExpired'))
      return
    }
    setUnlinking(true)
    const res = await unlinkGoogle(token)
    setUnlinking(false)
    if (!res.ok) {
      toast.error(
        res.code === 'cannot_unlink_last_method'
          ? t('unlinkLastMethod')
          : t('saveError'),
      )
      return
    }
    toast.success(t('unlinkedToast'))
    await refreshIdentity()
    await loadProfile()
  }

  /** Maps a failed email-change call to copy. Never renders `res.error`. */
  const emailChangeErrorCopy = (res: {
    code?: string
    status?: number
  }): string => {
    if (res.status === 429) return t('emailChange.rateLimited')
    switch (res.code) {
      case 'same_email':
        return t('emailChange.sameEmail')
      case 'email_taken':
        return t('emailChange.taken')
      case 'email_not_allowed':
      case 'signup_domain_not_allowed':
        return t('emailChange.notAllowed')
      case 'invalid_credentials':
        return t('emailChange.wrongCode')
      case 'email_change_expired':
        return t('emailChange.expired')
      default:
        return t('saveError')
    }
  }

  const sendEmailCode = async (options: { resend?: boolean } = {}) => {
    const target = newEmail.trim()
    if (!EMAIL_SHAPE.test(target)) {
      toast.error(t('emailChange.invalidEmail'))
      return
    }
    const token = await getToken()
    if (!token) {
      toast.error(t('sessionExpired'))
      return
    }
    setRequestingEmail(true)
    const res = await requestEmailChange(token, target)
    setRequestingEmail(false)
    if (!res.ok) {
      toast.error(emailChangeErrorCopy(res))
      return
    }
    setEmailStep('code')
    setEmailCode('')
    setEmailResendIn(EMAIL_CODE_RESEND_SECONDS)
    if (options.resend) toast.success(t('emailChange.codeResent'))
  }

  const handleConfirmEmailChange = async () => {
    const token = await getToken()
    if (!token) {
      toast.error(t('sessionExpired'))
      return
    }
    setConfirmingEmail(true)
    const res = await confirmEmailChange(token, emailCode)
    setConfirmingEmail(false)
    if (!res.ok) {
      toast.error(emailChangeErrorCopy(res))
      setEmailCode('')
      return
    }
    // Every session was revoked — sign out and land on /sign-in, same as a
    // password change. Nothing else needs refreshing: the whole session,
    // including the email claim inside the access token, is being replaced.
    toast.success(t('emailChange.changed'))
    onOpenChange(false)
    signOut('/sign-in')
  }

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error(t('passwordMismatch'))
      return
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      toast.error(t('passwordTooShort'))
      return
    }
    const token = await getToken()
    if (!token) {
      toast.error(t('sessionExpired'))
      return
    }
    setChangingPassword(true)
    const res = await changePassword(token, currentPassword, newPassword)
    setChangingPassword(false)
    if (!res.ok) {
      const msg =
        res.code === 'invalid_current_password'
          ? t('wrongCurrentPassword')
          : res.code === 'no_password_set'
            ? t('noPasswordSet')
            : res.code === 'password_policy'
              ? t('passwordTooShort')
              : t('saveError')
      toast.error(msg)
      return
    }
    // Every session was revoked — sign out and land on /sign-in.
    toast.success(t('passwordChanged'))
    onOpenChange(false)
    signOut('/sign-in')
  }

  const handleSetPassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error(t('passwordMismatch'))
      return
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      toast.error(t('passwordTooShort'))
      return
    }
    const token = await getToken()
    if (!token) {
      toast.error(t('sessionExpired'))
      return
    }
    setChangingPassword(true)
    const res = await setPassword(token, newPassword)
    setChangingPassword(false)
    if (!res.ok) {
      const msg =
        res.code === 'password_policy'
          ? t('passwordTooShort')
          : t('saveError')
      toast.error(msg)
      return
    }
    // Minting the first credential revokes every session — sign out.
    toast.success(t('passwordSetToast'))
    onOpenChange(false)
    signOut('/sign-in')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Pin width (the base DialogContent is w-fit) and floor the panel height
          so the box does not resize when switching between the Profile and
          Password tabs, which have different content lengths. */}
      <DialogContent className="w-full max-w-[calc(100vw-2rem)] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="profile">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="profile">{t('tabProfile')}</TabsTrigger>
            <TabsTrigger value="password">{t('tabPassword')}</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="min-h-80 space-y-4 pt-2">
            <div className="space-y-2">
              <Label>{t('emailLabel')}</Label>
              {emailStep === 'idle' && (
                <div className="flex items-center justify-between gap-2">
                  <p className="text-muted-foreground truncate text-sm">
                    {email || user?.email}
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="shrink-0"
                    onClick={() => {
                      setNewEmail('')
                      setEmailStep('enter')
                    }}
                  >
                    {t('changeEmail')}
                  </Button>
                </div>
              )}

              {emailStep === 'enter' && (
                <div className="space-y-2">
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder={t('emailChange.newEmailLabel')}
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    disabled={requestingEmail}
                  />
                  <p className="text-muted-foreground text-xs">
                    {t('emailChange.hint')}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => void sendEmailCode()}
                      disabled={requestingEmail || !newEmail.trim()}
                    >
                      {requestingEmail
                        ? t('saving')
                        : t('emailChange.continue')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setEmailStep('idle')}
                      disabled={requestingEmail}
                    >
                      {t('emailChange.cancel')}
                    </Button>
                  </div>
                </div>
              )}

              {emailStep === 'code' && (
                <div className="space-y-3">
                  <p className="text-muted-foreground text-sm">
                    {t('emailChange.codeSentTo', { email: newEmail.trim() })}
                  </p>
                  <InputOTP
                    maxLength={EMAIL_CODE_LENGTH}
                    value={emailCode}
                    onChange={setEmailCode}
                    disabled={confirmingEmail}
                    aria-label={t('emailChange.codeLabel')}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => void handleConfirmEmailChange()}
                      disabled={
                        confirmingEmail ||
                        emailCode.length !== EMAIL_CODE_LENGTH
                      }
                    >
                      {confirmingEmail
                        ? t('saving')
                        : t('emailChange.confirm')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => void sendEmailCode({ resend: true })}
                      disabled={
                        requestingEmail ||
                        confirmingEmail ||
                        emailResendIn > 0
                      }
                    >
                      {emailResendIn > 0
                        ? t('emailChange.resendIn', { seconds: emailResendIn })
                        : t('emailChange.resend')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setEmailStep('idle')}
                      disabled={confirmingEmail}
                    >
                      {t('emailChange.cancel')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              <Avatar className="size-16 rounded-full">
                {avatarUrl ? (
                  <AvatarImage src={avatarUrl} alt={firstName} />
                ) : null}
                <AvatarFallback className="rounded-full text-lg">
                  {initial}
                </AvatarFallback>
              </Avatar>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={handleAvatarSelect}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={uploadingAvatar}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploadingAvatar ? t('uploading') : t('changeAvatar')}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="acct-first-name">{t('firstName')}</Label>
              <Input
                id="acct-first-name"
                value={firstName}
                onChange={event => setFirstName(event.target.value)}
                disabled={profileLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="acct-last-name">{t('lastName')}</Label>
              <Input
                id="acct-last-name"
                value={lastName}
                onChange={event => setLastName(event.target.value)}
                disabled={profileLoading}
              />
            </div>
            <Button
              type="button"
              className="w-full"
              disabled={savingProfile || profileLoading}
              onClick={handleSaveProfile}
            >
              {savingProfile ? t('saving') : t('save')}
            </Button>

            <div className="space-y-2 border-t pt-4">
              <Label>{t('googleSection')}</Label>
              {googleIdentity ? (
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground flex min-w-0 items-center gap-2 text-sm">
                    <GoogleIcon />
                    <span className="truncate">
                      {t('googleLinked', {
                        email: googleIdentity.email ?? '',
                      })}
                    </span>
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={unlinking || profileLoading}
                    onClick={handleUnlinkGoogle}
                  >
                    {unlinking ? t('unlinking') : t('unlinkGoogle')}
                  </Button>
                </div>
              ) : (
                <>
                  <p className="text-muted-foreground text-sm">
                    {t('googleNotLinked')}
                  </p>
                  <Button asChild variant="outline" className="w-full">
                    <a href={linkGoogleHref}>
                      <GoogleIcon />
                      {t('linkGoogle')}
                    </a>
                  </Button>
                </>
              )}
            </div>
          </TabsContent>

          <TabsContent value="password" className="min-h-80 space-y-4 pt-2">
            {hasPassword === null ? (
              <p className="text-muted-foreground text-sm">{t('loading')}</p>
            ) : hasPassword ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="acct-current-pw">
                    {t('currentPassword')}
                  </Label>
                  <Input
                    id="acct-current-pw"
                    type="password"
                    value={currentPassword}
                    onChange={event => setCurrentPassword(event.target.value)}
                    autoComplete="current-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="acct-new-pw">{t('newPassword')}</Label>
                  <Input
                    id="acct-new-pw"
                    type="password"
                    value={newPassword}
                    onChange={event => setNewPassword(event.target.value)}
                    autoComplete="new-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="acct-confirm-pw">
                    {t('confirmPassword')}
                  </Label>
                  <Input
                    id="acct-confirm-pw"
                    type="password"
                    value={confirmPassword}
                    onChange={event => setConfirmPassword(event.target.value)}
                    autoComplete="new-password"
                  />
                </div>
                <p className="text-muted-foreground text-xs">
                  {t('passwordHint')}
                </p>
                <Button
                  type="button"
                  className="w-full"
                  disabled={
                    changingPassword || !currentPassword || !newPassword
                  }
                  onClick={handleChangePassword}
                >
                  {changingPassword ? t('saving') : t('changePassword')}
                </Button>
                <div className="space-y-1">
                  <a
                    href="/reset-password"
                    className="text-sm font-medium underline underline-offset-4"
                  >
                    {t('forgotPassword')}
                  </a>
                  <p className="text-muted-foreground text-xs">
                    {t('resetPasswordHint')}
                  </p>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm font-medium">{t('setPasswordTitle')}</p>
                <div className="space-y-2">
                  <Label htmlFor="acct-set-new-pw">{t('newPassword')}</Label>
                  <Input
                    id="acct-set-new-pw"
                    type="password"
                    value={newPassword}
                    onChange={event => setNewPassword(event.target.value)}
                    autoComplete="new-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="acct-set-confirm-pw">
                    {t('confirmPassword')}
                  </Label>
                  <Input
                    id="acct-set-confirm-pw"
                    type="password"
                    value={confirmPassword}
                    onChange={event => setConfirmPassword(event.target.value)}
                    autoComplete="new-password"
                  />
                </div>
                <p className="text-muted-foreground text-xs">
                  {t('setPasswordHint')}
                </p>
                <p className="text-muted-foreground text-xs">
                  {t('passwordSetLogoutNote')}
                </p>
                <Button
                  type="button"
                  className="w-full"
                  disabled={changingPassword || !newPassword}
                  onClick={handleSetPassword}
                >
                  {changingPassword ? t('saving') : t('setPassword')}
                </Button>
              </>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
